/**
 * loaderNormalJson_Inheritance.ts - JSON Inheritance Support methods for LoaderNormalJson
 * Uses context stack for inheritance resolution (supports multi-parent scenarios)
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { InheritanceOperation } from '../../../../Assembler/dist/model/index.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/index.js';
import { JsonValue, JsonValueType } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { LoaderNormalJson } from './loaderNormalJson.js';


type PlainJsonObject = { [key: string]: unknown };
type PlainJsonMap = globalThis.Map <string, unknown>;

/**
 * Cache entry stored in dataJsonCache
 */
interface DataJsonCacheEntry {
    relativePath: string;
    jsonStructure: JsonObject;
}

type DataJsonCache = Map<string, Map<string, DataJsonCacheEntry>>;

/**
 * Result type for inheritance resolution
 */
interface ResolveResult {
    resolvedValue: string | null;
    sourceTemplate: string | null;
    wasInherited: boolean;
}

/**
 * Result type for context stack search
 */
interface SearchResult {
    value: string | null;
    sourceTemplate: string | null;
}


/**
 * Resolves a DataJson key by searching up the context stack (supports multi-parent scenarios)
 */
export function resolveDataJsonKeyWithInheritance(
    actualKey: string,
    defaultValue: string,
    currentTemplateKey: string,
    dataJsonCache: DataJsonCache,
    primaryAppSite: string,
    flowStack: string[] | null = null,
    resolvedTemplateJson: JsonObject | null = null
): ResolveResult {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`Resolving inherited key: ${actualKey} for template ${currentTemplateKey}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderNormalJson');
    }

    // Use context stack for inheritance resolution (supports multi-parent scenarios)
    if (flowStack && flowStack.length > 0) {
        const { value: inheritedValue, sourceTemplate } = searchContextStackForKey(actualKey, flowStack, dataJsonCache, primaryAppSite, resolvedTemplateJson);
        if (inheritedValue !== null) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Found inherited value for ${actualKey}: ${inheritedValue} from ${sourceTemplate}`, 'LoaderNormalJson');
            }
            return { resolvedValue: inheritedValue, sourceTemplate, wasInherited: true };
        }
    }

    // If not found in context stack, use the default value
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`No inherited value found for ${actualKey}, using default: ${defaultValue}`, 'LoaderNormalJson');
    }
    return { resolvedValue: defaultValue, sourceTemplate: null, wasInherited: false };
}

/**
 * Searches up the context stack to find a DataJson key value
 */
export function searchContextStackForKey(
    key: string,
    flowStack: string[],
    dataJsonCache: DataJsonCache,
    primaryAppSite: string,
    resolvedTemplateJson: JsonObject | null = null
): SearchResult {
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`SearchContextStackForKey: key=${key}, flowStack=[${flowStack.join(',')}]`, 'LoaderNormalJson');
    }

    const keyLower = key.toLowerCase();

    // Walk UP through context stack (from end toward beginning)
    for (let i = flowStack.length - 1; i >= 0; i--) {
        const parentName = flowStack[i];
        const parentNameLower = parentName.toLowerCase();

        // FIRST: Check resolved template JSON (has DynamicData results)
        const resolvedKey = `${primaryAppSite.toLowerCase()}_${parentNameLower}`;
        if (resolvedTemplateJson && resolvedTemplateJson.has(resolvedKey)) {
            const resolvedJson = resolvedTemplateJson.get(resolvedKey).asObject();
            if (resolvedJson) {
                // Handle both Map (JsonObject) and plain object
                const entries: Iterable<[string, unknown]> = resolvedJson instanceof Map ? resolvedJson.entries() : Object.entries(resolvedJson);
                for (const [jsonKey, jsonValue] of entries) {
                    if (jsonKey.toLowerCase() === keyLower) {
                        // Extract string value - handle both raw strings and JsonValue wrappers
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                Logger.debug(`Found key ${key} in resolved context at ${parentName}: ${strValue}`, 'LoaderNormalJson');
                            }
                            return { value: strValue, sourceTemplate: parentName };
                        }
                    }
                }
            }
        }

        // SECOND: Fall back to original cache (static values from disk)
        if (dataJsonCache.has(primaryAppSite)) {
            const parentDataJsonCache = dataJsonCache.get(primaryAppSite)!;
            const parentDataKey = `${primaryAppSite.toLowerCase()}_${parentNameLower}`;

            if (parentDataJsonCache.has(parentDataKey)) {
                const parentTargetFile = parentDataJsonCache.get(parentDataKey)!;

                if (parentTargetFile && parentTargetFile.jsonStructure) {
                    // Handle both Map (JsonObject) and plain object
                    const jsonEntries: Iterable<[string, unknown]> = parentTargetFile.jsonStructure instanceof Map ? (parentTargetFile.jsonStructure as unknown as PlainJsonMap).entries() : Object.entries(parentTargetFile.jsonStructure);
                    for (const [jsonKey, jsonValue] of jsonEntries) {
                        if (jsonKey.toLowerCase() === keyLower) {
                            let strValue: string | null = null;
                            if (typeof jsonValue === 'string') {
                                strValue = jsonValue;
                            } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                                strValue = jsonValue.asString();
                            }
                            if (strValue !== null) {
                                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                                    Logger.debug(`Found key ${key} in original cache at ${parentName}: ${strValue}`, 'LoaderNormalJson');
                                }
                                return { value: strValue, sourceTemplate: parentName };
                            }
                        }
                    }
                }
            }
        }
    }

    // Not found in context stack
    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`Key ${key} not found in context stack`, 'LoaderNormalJson');
    }
    return { value: null, sourceTemplate: null };
}

/**
 * Builds an immutable parent context map from the flow stack.
 */
export function buildParentContext(
    flowStack: string[],
    dataJsonCache: DataJsonCache,
    primaryAppSite: string,
    resolvedTemplateJson: JsonObject | null = null
): Map<string, string> | null {
    if (!flowStack || flowStack.length === 0) {
        return null;
    }

    const context = new Map<string, string>();

    for (let i = 0; i < flowStack.length; i++) {
        const parentName = flowStack[i];
        const parentNameLower = parentName.toLowerCase();

        // FIRST: Static cache values from DataJson TemplateStore
        if (dataJsonCache && dataJsonCache.has(primaryAppSite)) {
            const parentDataJsonCache = dataJsonCache.get(primaryAppSite)!;
            const bpcParentKey = `${primaryAppSite.toLowerCase()}_${parentNameLower}`;
            if (parentDataJsonCache.has(bpcParentKey)) {
                const parentTargetFile = parentDataJsonCache.get(bpcParentKey)!;
                if (parentTargetFile && parentTargetFile.jsonStructure) {
                    const jsonEntries: Iterable<[string, unknown]> = parentTargetFile.jsonStructure instanceof Map ? (parentTargetFile.jsonStructure as unknown as PlainJsonMap).entries() : Object.entries(parentTargetFile.jsonStructure);
                    for (const [jsonKey, jsonValue] of jsonEntries) {
                        let strValue: string | null = null;
                        if (typeof jsonValue === 'string') {
                            strValue = jsonValue;
                        } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                            strValue = jsonValue.asString();
                        }
                        if (strValue !== null) {
                            context.set(jsonKey.toLowerCase(), strValue);
                        }
                    }
                }
            }
        }

        // SECOND: Resolved values overwrite static
        const bpcResolvedKey = `${primaryAppSite.toLowerCase()}_${parentNameLower}`;
        if (resolvedTemplateJson && resolvedTemplateJson.has(bpcResolvedKey)) {
            const resolvedJson = resolvedTemplateJson.get(bpcResolvedKey).asObject();
            if (resolvedJson) {
                const entries: Iterable<[string, unknown]> = resolvedJson instanceof Map ? resolvedJson.entries() : Object.entries(resolvedJson);
                for (const [jsonKey, jsonValue] of entries) {
                    let strValue: string | null = null;
                    if (typeof jsonValue === 'string') {
                        strValue = jsonValue;
                    } else if (jsonValue instanceof JsonValue && jsonValue.typeName === JsonValueType.String) {
                        strValue = jsonValue.asString();
                    }
                    if (strValue !== null) {
                        context.set(jsonKey.toLowerCase(), strValue);
                    }
                }
            }
        }
    }

    return context.size > 0 ? context : null;
}

export const inheritanceMethods = {
    hasGlobalData(this: LoaderNormalJson, appSite: string, componentName: string, resolvedAppSite: string | null): boolean {
        const targetAppSite = resolvedAppSite || appSite;
        return this.getGlobalDataWithFallback(targetAppSite, appSite) !== null
            || this.getGlobalViewDataForComponentWithFallback(targetAppSite, appSite, componentName) !== null;
    },

    getTemplateDataWithInheritance(
        this: LoaderNormalJson,
        appSite: string,
        appView: string,
        appFile: string,
        dataJson: JsonObject,
        resolvedTemplateName: string,
        dataJsonCache: DataJsonCache,
        maxRecursionDepth: number,
        flowStack: string[] | null = null,
        loader: LoaderNormalJson | null = null,
        primaryAppSite: string | null = null,
        resolvedAppSite: string | null = null,
        configJson: JsonObject | null = null,
        htmlJson: JsonObject | null = null,
        clientMode: boolean = false
    ): JsonObject | null {
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`_getTemplateDataJsonWithInheritance: appSite=${appSite}, resolvedTemplateName=${resolvedTemplateName}, flowStack=${flowStack ? flowStack.length : 0}`, 'LoaderNormalJson');
        }

        if (!dataJson || dataJson.size === 0) {
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`No DataJson found for resolvedTemplateName=${resolvedTemplateName}`, 'LoaderNormalJson');
            }
            return null;
        }

        const templateKey = `${appSite.toLowerCase()}_${resolvedTemplateName.toLowerCase()}`;
        const resolvedJson = new JsonObject();
        const dataJsonEntries: Iterable<[string, unknown]> = dataJson instanceof Map ? (dataJson.entries() as unknown as Iterable<[string, unknown]>) : Object.entries(dataJson);
        for (const [key, rawValue] of dataJsonEntries) {
            const value = rawValue instanceof JsonValue ? (rawValue.asString() ?? rawValue) : rawValue;
            if (key.endsWith('#') && typeof value === 'string') {
                const actualKey = key.substring(0, key.length - 1);
                const { resolvedValue, sourceTemplate, wasInherited } = resolveDataJsonKeyWithInheritance(actualKey, value, templateKey, dataJsonCache, primaryAppSite || appSite, flowStack, loader ? loader._dynamicResultsJson.resolvedTemplateJson : null);
                if (resolvedValue !== null) {
                    resolvedJson.set(actualKey, resolvedValue);
                    if (loader && loader._inheritanceOperations) {
                        loader._inheritanceOperations.push(new InheritanceOperation({
                            targetTemplate: resolvedTemplateName,
                            contextPath: flowStack ? [...flowStack] : [],
                            inheritKey: actualKey,
                            sourceTemplate: sourceTemplate || '',
                            defaultValue: value,
                            resolvedValue: resolvedValue,
                            wasInherited: wasInherited
                        }));
                    }
                    continue;
                }
            }

            resolvedJson.set(key, rawValue);
        }

        if (loader) {
            const componentDepth = flowStack?.length ?? 0;
            loader.applyGlobalData(appSite, resolvedTemplateName, resolvedJson, resolvedAppSite);
            loader.applyDynamicDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, appSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
            // Client-data preparer (dynamicclientdata.json): produce the RAW set for browser-side filtering into
            // dynamicClientDataResults. Runs alongside the server data handlers but never mutates resolvedJson.
            loader.applyDynamicClientDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, appSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
            // CLIENT tier consumption: fill resolvedJson from the delivered island via the registered client handler
            // (substitutes for the server data handler that ConfigClient does not ship) and re-emit the island at the
            // page tail — so the client re-merge matches the server render byte-for-byte.
            if (clientMode) {
                loader.applyClientHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, appSite, resolvedTemplateName, htmlJson, resolvedJson, configJson, flowStack);
            }
            if (loader.getActionMode()) {
                loader.applyDynamicActionHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, appSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
                loader.applyDynamicDataHandlers(appSite, appView, appFile, resolvedTemplateName, componentDepth, appSite, resolvedTemplateName, resolvedJson, configJson, flowStack);
            }
            loader._dynamicResultsJson.resolvedTemplateJson.set(`${appSite.toLowerCase()}_${resolvedTemplateName.toLowerCase()}`, resolvedJson);
        }

        return resolvedJson;
    },

    applyGlobalData(this: LoaderNormalJson, appSite: string, componentName: string, componentJson: JsonObject, resolvedAppSite: string | null): void {
        const targetAppSite = resolvedAppSite || appSite;
        const globalData = this.getGlobalDataWithFallback(targetAppSite, appSite);
        const globalViewData = this.getGlobalViewDataForComponentWithFallback(targetAppSite, appSite, componentName);

        this.mergeGlobalDataIntoComponent(componentJson, globalData);
        this.mergeGlobalDataIntoComponent(componentJson, globalViewData);
    },

    getWithFallback(this: LoaderNormalJson, targetAppSite: string, appSite: string, getter: (site: string) => JsonObject | PlainJsonObject | null): JsonObject | null {
        if (!this._config!.globalDataMergeFromFallback) {
            let result = getter(targetAppSite);
            if (result !== null) {
                return result as JsonObject;
            }

            if (targetAppSite !== appSite) {
                result = getter(appSite);
                if (result !== null) {
                    return result as JsonObject;
                }
            }

            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites && searchAppSites.length > 0) {
                const searches = searchAppSites.split(',');
                for (let i = 0; i < searches.length; i++) {
                    const trimmed = searches[i].trim();
                    if (!trimmed) {
                        continue;
                    }

                    result = getter(trimmed);
                    if (result !== null) {
                        return result as JsonObject;
                    }
                }
            }

            return null;
        }

        const sources: Array<JsonObject | PlainJsonObject> = [];
        const targetData = getter(targetAppSite);
        if (targetData !== null) {
            sources.push(targetData);
        }

        if (targetAppSite !== appSite) {
            const appSiteData = getter(appSite);
            if (appSiteData !== null) {
                sources.push(appSiteData);
            }
        }

        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites && searchAppSites.length > 0) {
            const searches = searchAppSites.split(',');
            for (let i = 0; i < searches.length; i++) {
                const trimmed = searches[i].trim();
                if (!trimmed) {
                    continue;
                }

                const searchData = getter(trimmed);
                if (searchData !== null) {
                    sources.push(searchData);
                }
            }
        }

        if (sources.length === 0) {
            return null;
        }

        if (sources.length === 1) {
            return sources[0] as JsonObject;
        }

        const merged = new JsonObject();
        const overwrite = this._config!.globalDataMergeOverwrite;
        for (let i = 0; i < sources.length; i++) {
            const source = sources[i];
            const entries: Iterable<[string, unknown]> = source instanceof Map ? source.entries() : Object.entries(source);
            for (const [key, value] of entries) {
                if (!merged.has(key) || overwrite) {
                    merged.set(key, value);
                }
            }
        }

        return merged;
    },

    getGlobalDataWithFallback(this: LoaderNormalJson, targetAppSite: string, appSite: string): JsonObject | null {
        if (!this._config!.globalDataMergeFromFallback) {
            let result = this.getGlobalData(targetAppSite);
            if (result !== null) return result as JsonObject;
            if (targetAppSite !== appSite) {
                result = this.getGlobalData(appSite);
                if (result !== null) return result as JsonObject;
            }
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites && searchAppSites.length > 0) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    result = this.getGlobalData(trimmed);
                    if (result !== null) return result as JsonObject;
                }
            }
            return null;
        }

        const sources: Array<JsonObject | PlainJsonObject> = [];
        const targetData = this.getGlobalData(targetAppSite);
        if (targetData !== null) sources.push(targetData);
        if (targetAppSite !== appSite) {
            const appSiteData = this.getGlobalData(appSite);
            if (appSiteData !== null) sources.push(appSiteData);
        }
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites && searchAppSites.length > 0) {
            for (const search of searchAppSites.split(',')) {
                const trimmed = search.trim();
                if (!trimmed) continue;
                const searchData = this.getGlobalData(trimmed);
                if (searchData !== null) sources.push(searchData);
            }
        }
        if (sources.length === 0) return null;
        if (sources.length === 1) return sources[0] as JsonObject;

        const merged = new JsonObject();
        const overwrite = this._config!.globalDataMergeOverwrite;
        for (const source of sources) {
            const entries: Iterable<[string, unknown]> = source instanceof Map ? source.entries() : Object.entries(source);
            for (const [key, value] of entries) {
                if (!merged.has(key) || overwrite) {
                    merged.set(key, value);
                }
            }
        }
        return merged;
    },

    getGlobalViewDataForComponentWithFallback(this: LoaderNormalJson, targetAppSite: string, appSite: string, componentName: string): JsonObject | null {
        if (!this._config!.globalDataMergeFromFallback) {
            let result = this.getGlobalViewDataForComponent(targetAppSite, componentName);
            if (result !== null) return result as JsonObject;
            if (targetAppSite !== appSite) {
                result = this.getGlobalViewDataForComponent(appSite, componentName);
                if (result !== null) return result as JsonObject;
            }
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites && searchAppSites.length > 0) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    result = this.getGlobalViewDataForComponent(trimmed, componentName);
                    if (result !== null) return result as JsonObject;
                }
            }
            return null;
        }

        const sources: Array<JsonObject | PlainJsonObject> = [];
        const targetData = this.getGlobalViewDataForComponent(targetAppSite, componentName);
        if (targetData !== null) sources.push(targetData);
        if (targetAppSite !== appSite) {
            const appSiteData = this.getGlobalViewDataForComponent(appSite, componentName);
            if (appSiteData !== null) sources.push(appSiteData);
        }
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites && searchAppSites.length > 0) {
            for (const search of searchAppSites.split(',')) {
                const trimmed = search.trim();
                if (!trimmed) continue;
                const searchData = this.getGlobalViewDataForComponent(trimmed, componentName);
                if (searchData !== null) sources.push(searchData);
            }
        }
        if (sources.length === 0) return null;
        if (sources.length === 1) return sources[0] as JsonObject;

        const merged = new JsonObject();
        const overwrite = this._config!.globalDataMergeOverwrite;
        for (const source of sources) {
            const entries: Iterable<[string, unknown]> = source instanceof Map ? source.entries() : Object.entries(source);
            for (const [key, value] of entries) {
                if (!merged.has(key) || overwrite) {
                    merged.set(key, value);
                }
            }
        }
        return merged;
    },

    findKeyIgnoreCase(this: LoaderNormalJson, data: JsonObject, key: string): string | null {
        if (data.has(key)) {
            return key;
        }

        const keyLower = key.toLowerCase();
        for (const existingKey of data.keys()) {
            if (existingKey.toLowerCase() === keyLower) {
                return existingKey;
            }
        }

        return null;
    },

    getScalarString(this: LoaderNormalJson, val: JsonValue): string | null {
        if (val.typeName === JsonValueType.String) {
            return val.asString();
        }
        if (val.typeName === JsonValueType.Integer) {
            return val.asInteger()?.toString() ?? null;
        }
        if (val.typeName === JsonValueType.Number) {
            return val.asNumber()?.toString() ?? null;
        }
        if (val.typeName === JsonValueType.Bool) {
            return val.asBool() === true ? 'true' : 'false';
        }

        return null;
    },

    matchesByStringKeys(
        this: LoaderNormalJson,
        componentItem: PlainJsonObject | PlainJsonMap,
        globalItem: PlainJsonObject | PlainJsonMap,
        primaryKey: string | null
    ): boolean {
        const getStr = (obj: PlainJsonObject | PlainJsonMap, k: string): string | null => {
            if (obj instanceof Map) {
                if (!obj.has(k)) return null;
                const v = obj.get(k);
                if (v instanceof JsonValue) return this.getScalarString(v);
                if (typeof v === 'string') return v;
                if (typeof v === 'number') return v.toString();
                if (typeof v === 'boolean') return v ? 'true' : 'false';
                return null;
            }
            const record = obj as PlainJsonObject;
            const v = record[k];
            if (v === undefined) return null;
            if (v instanceof JsonValue) return this.getScalarString(v);
            if (typeof v === 'string') return v;
            if (typeof v === 'number') return v.toString();
            if (typeof v === 'boolean') return v ? 'true' : 'false';
            return null;
        };
        const getKeys = (obj: PlainJsonObject | PlainJsonMap): string[] => obj instanceof Map ? Array.from(obj.keys()) : Object.keys(obj);
        const hasKey = (obj: PlainJsonObject | PlainJsonMap, k: string): boolean => obj instanceof Map ? obj.has(k) : k in obj;

        if (primaryKey !== null) {
            const compVal = getStr(componentItem, primaryKey);
            const globalVal = getStr(globalItem, primaryKey);
            return compVal !== null && globalVal !== null && compVal === globalVal;
        }

        let hasStringKey = false;
        for (const key of getKeys(globalItem)) {
            const globalStr = getStr(globalItem, key);
            if (globalStr === null) continue;
            hasStringKey = true;
            if (!hasKey(componentItem, key)) return false;
            const compStr = getStr(componentItem, key);
            if (compStr === null || compStr !== globalStr) return false;
        }

        return hasStringKey;
    },

    mergeGlobalDataIntoComponent(
        this: LoaderNormalJson,
        componentJson: JsonObject | PlainJsonObject,
        globalData: JsonObject | PlainJsonObject | null
    ): void {
        if (globalData === null) {
            return;
        }

        const globalDataKeys = globalData instanceof Map ? Array.from(globalData.keys()) : Object.keys(globalData);
        if (globalDataKeys.length === 0) {
            return;
        }

        const isMap = globalData instanceof Map;
        const hasKey = (k: string): boolean => isMap ? (globalData as PlainJsonMap).has(k) : Object.prototype.hasOwnProperty.call(globalData, k);
        const getValueByKey = (k: string): unknown => {
            if (typeof (globalData as { getValue?: (k: string) => unknown }).getValue === 'function') {
                return (globalData as { getValue: (k: string) => unknown }).getValue(k);
            }
            return isMap ? (globalData as PlainJsonMap).get(k) : (globalData as PlainJsonObject)[k];
        };
        const getAllKeys = (): string[] => isMap ? Array.from((globalData as PlainJsonMap).keys()) : Object.keys(globalData as PlainJsonObject);
        const findKeyIgnoreCase = (k: string): string | null => {
            if (hasKey(k)) return k;
            const lowerKey = k.toLowerCase();
            const keys = getAllKeys();
            for (let j = 0; j < keys.length; j++) {
                if (keys[j].toLowerCase() === lowerKey) return keys[j];
            }
            return null;
        };

        const setCompValue = (key: string, value: unknown): void => {
            if (componentJson instanceof Map) {
                componentJson.set(key, value);
            } else {
                (componentJson as PlainJsonObject)[key] = value;
            }
        };
        const getCompValue = (key: string): unknown =>
            componentJson instanceof Map ? componentJson.get(key) : (componentJson as PlainJsonObject)[key];
        const hasCompKey = (key: string): boolean =>
            componentJson instanceof Map ? componentJson.has(key) : Object.prototype.hasOwnProperty.call(componentJson, key);

        const keysToProcess: string[] = componentJson instanceof Map ? Array.from(componentJson.keys()) : Object.keys(componentJson);

        for (let i = 0; i < keysToProcess.length; i++) {
            const key = keysToProcess[i];
            const rawValue: unknown = getCompValue(key);

            let strValue: string | null = null;
            if (rawValue instanceof JsonValue) {
                if (rawValue.typeName === JsonValueType.String) strValue = rawValue.asString();
            } else if (typeof rawValue === 'string') {
                strValue = rawValue;
            }

            if (strValue !== null) {
                // Full placeholder: entire value is "{{Key}}" -> replace with typed global value
                if (strValue.startsWith('{{') && strValue.endsWith('}}') && strValue.length >= 4) {
                    const placeholderKey = strValue.substring(2, strValue.length - 2);
                    const matchKey = findKeyIgnoreCase(placeholderKey);
                    if (matchKey !== null) {
                        setCompValue(key, getValueByKey(matchKey));
                    }
                    continue;
                }
                // Embedded placeholders: inline replace any {{Key}} segments with string-typed global values
                if (strValue.includes('{{')) {
                    let result = strValue;
                    let startIdx = 0;
                    while ((startIdx = result.indexOf('{{', startIdx)) >= 0) {
                        const endIdx = result.indexOf('}}', startIdx + 2);
                        if (endIdx < 0) break;
                        const placeholderKey = result.substring(startIdx + 2, endIdx);
                        const matchKey = findKeyIgnoreCase(placeholderKey);
                        if (matchKey !== null) {
                            const globalValue = getValueByKey(matchKey);
                            let replacement: string | null = null;
                            if (globalValue instanceof JsonValue) {
                                if (globalValue.typeName === JsonValueType.String) replacement = globalValue.asString();
                            } else if (typeof globalValue === 'string') {
                                replacement = globalValue;
                            }
                            if (replacement !== null) {
                                result = result.substring(0, startIdx) + replacement + result.substring(endIdx + 2);
                                startIdx += replacement.length;
                                continue;
                            }
                        }
                        startIdx = endIdx + 2;
                    }
                    if (result !== strValue) {
                        setCompValue(key, result);
                    }
                }
                continue;
            }

            // Array merge: if componentJson[key] is an array and globalData has an array under
            // the same key (case-insensitive), append items from globalData that don't already
            // exist in the component array (matched by primaryKey or by all scalar keys).
            let isArray = false;
            if (rawValue instanceof JsonValue && rawValue.typeName === JsonValueType.Array) {
                isArray = true;
            } else if (Array.isArray(rawValue)) {
                isArray = true;
            }
            if (!isArray) continue;

            const resolvedArrayKey = findKeyIgnoreCase(key);
            if (resolvedArrayKey === null) continue;

            const globalValue: unknown = getValueByKey(resolvedArrayKey);
            let globalIsArray = false;
            if (globalValue instanceof JsonValue && globalValue.typeName === JsonValueType.Array) {
                globalIsArray = true;
            } else if (Array.isArray(globalValue)) {
                globalIsArray = true;
            }
            if (!globalIsArray) continue;

            const componentArray: unknown[] | null = rawValue instanceof JsonValue
                ? (rawValue.asArray() as unknown as unknown[])
                : (rawValue as unknown[]);
            const globalArray: unknown[] | null = globalValue instanceof JsonValue
                ? ((globalValue as JsonValue).asArray() as unknown as unknown[])
                : (globalValue as unknown[]);
            if (!componentArray || !globalArray) continue;

            // Optional "<ArrayName>Primary" sibling holds the scalar key used to match items.
            const primaryKeyName = key + 'Primary';
            let primaryKey: string | null = null;
            if (hasCompKey(primaryKeyName)) {
                const pkRawValue: unknown = getCompValue(primaryKeyName);
                const pkStr = pkRawValue instanceof JsonValue
                    ? pkRawValue.asString()
                    : (typeof pkRawValue === 'string' ? pkRawValue : null);
                if (pkStr) primaryKey = pkStr;
            }

            const newArray = componentArray.slice();
            for (let g = 0; g < globalArray.length; g++) {
                const globalItem = globalArray[g];
                const globalObj = globalItem instanceof JsonValue
                    ? globalItem.asObject()
                    : (typeof globalItem === 'object' && globalItem !== null && !Array.isArray(globalItem) ? globalItem : null);
                if (!globalObj) continue;

                let existsInComponent = false;
                for (let c = 0; c < newArray.length; c++) {
                    const compItem = newArray[c];
                    const compObj = compItem instanceof JsonValue
                        ? compItem.asObject()
                        : (typeof compItem === 'object' && compItem !== null && !Array.isArray(compItem) ? compItem : null);
                    if (!compObj) continue;
                    if (this.matchesByStringKeys(
                        compObj as PlainJsonObject | PlainJsonMap,
                        globalObj as PlainJsonObject | PlainJsonMap,
                        primaryKey
                    )) {
                        existsInComponent = true;
                        break;
                    }
                }

                if (!existsInComponent) {
                    newArray.push(globalItem);
                }
            }
            setCompValue(key, newArray);
        }
    }
};
